import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Paper, 
  Text, 
  Stack, 
  Loader, 
  Alert, 
  Tooltip,
  useMantineTheme,
  Box,
  Group,
  Badge,
  Progress,
  ActionIcon,
} from '@mantine/core';
import { 
  IconAlertCircle, 
  IconBook, 
  IconPlayerPlay,
  IconCheck,
  IconHourglass,
} from '@tabler/icons-react';
import { useAuth } from '../../auth/context/AuthContext';
import { courseService, StoredCourse } from '../services/courseService';
import { useCourse } from '../context/CourseContext';
import { CourseState } from '../types';

interface CourseHistoryProps {
  collapsed?: boolean;
}

const getStateColor = (state: CourseState) => {
  switch (state) {
    case 'not_started':
      return 'gray';
    case 'in_progress':
      return 'blue';
    case 'completed':
      return 'green';
    default:
      return 'gray';
  }
};

const getStateIcon = (state: CourseState) => {
  switch (state) {
    case 'not_started':
      return <IconPlayerPlay size={18} />;
    case 'in_progress':
      return <IconHourglass size={18} />;
    case 'completed':
      return <IconCheck size={18} />;
    default:
      return <IconPlayerPlay size={18} />;
  }
};

const CourseHistoryItem: React.FC<{ course: StoredCourse; collapsed: boolean }> = ({ course, collapsed }) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/course/${course.id}`, { state: { course } });
  };

  const getStateBorderColor = (state: CourseState) => {
    switch (state) {
      case 'in_progress':
        return theme.colors.blue[4];
      case 'completed':
        return theme.colors.green[4];
      case 'not_started':
      default:
        return theme.colors.gray[3];
    }
  };

  const getStateBackgroundColor = (state: CourseState) => {
    switch (state) {
      case 'in_progress':
        return theme.colors.blue[0];
      case 'completed':
        return theme.colors.green[0];
      case 'not_started':
      default:
        return theme.colors.gray[0];
    }
  };

  if (collapsed) {
    return (
      <Tooltip
        label={
          <div>
            <Text weight={500}>{course.input.topicName}</Text>
            <Text size="xs" color="dimmed">
              {course.state === 'in_progress' ? 'In Progress' : course.state === 'completed' ? 'Completed' : 'Not Started'}
            </Text>
          </div>
        }
        position="right"
        withArrow
        multiline
        width={200}
      >
        <ActionIcon
          variant="light"
          color={getStateColor(course.state)}
          onClick={handleClick}
          sx={{
            width: '100%',
            height: 42,
            marginBottom: theme.spacing.xs,
            transition: 'all 0.2s ease',
            backgroundColor: getStateBackgroundColor(course.state),
            border: `1px solid ${getStateBorderColor(course.state)}`,
            '&:hover': {
              transform: 'translateX(4px)',
              backgroundColor: getStateBackgroundColor(course.state),
            }
          }}
        >
          {getStateIcon(course.state)}
        </ActionIcon>
      </Tooltip>
    );
  }

  return (
    <Paper
      p="md"
      withBorder
      sx={{
        marginBottom: theme.spacing.xs,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: getStateBackgroundColor(course.state),
        borderColor: getStateBorderColor(course.state),
        '&:hover': {
          transform: 'translateX(4px)',
          boxShadow: theme.shadows.sm,
        },
      }}
      onClick={handleClick}
    >
      <Stack spacing="xs">
        <Group position="apart">
          <Text weight={500} size="sm">
            {course.input.topicName}
          </Text>
          <Badge
            color={getStateColor(course.state)}
            variant="dot"
            size="sm"
          >
            {course.state === 'in_progress' ? 'In Progress' : course.state === 'completed' ? 'Completed' : 'Not Started'}
          </Badge>
        </Group>
        {course.startedAt && (
          <Text size="xs" color="dimmed">
            Started: {course.startedAt.toLocaleDateString()}
          </Text>
        )}
        {course.state === 'in_progress' && (
          <Progress
            value={70}
            size="sm"
            color={getStateColor(course.state)}
            radius="xl"
          />
        )}
      </Stack>
    </Paper>
  );
};

const CourseHistory: React.FC<CourseHistoryProps> = ({ collapsed = false }) => {
  const theme = useMantineTheme();
  const { user } = useAuth();
  const { refreshTrigger } = useCourse();
  const [courses, setCourses] = useState<StoredCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCourses = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Update legacy courses first
        await courseService.updateLegacyCourses(user.uid);
        
        // Then load all courses
        const userCourses = await courseService.getUserCourses(user.uid);
        setCourses(userCourses);
      } catch (error) {
        console.error('Error loading courses:', error);
        setError('Failed to load course history');
      } finally {
        setLoading(false);
      }
    };

    loadCourses();
  }, [user, refreshTrigger]);

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <Stack align="center" spacing="md" p="xl">
        <Loader size="md" color="indigo" variant="dots" />
        <Text size="sm" color="dimmed">Loading your courses...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert 
        icon={<IconAlertCircle size={24} />} 
        title="Error" 
        color="red"
        variant="filled"
        radius="md"
        styles={{
          title: { fontSize: '1.1rem' },
        }}
      >
        {error}
      </Alert>
    );
  }

  if (courses.length === 0) {
    return (
      <Paper 
        p="xl" 
        radius="md" 
        withBorder
        sx={{
          borderColor: theme.colors.gray[3],
          background: theme.colors.gray[0],
        }}
      >
        <Stack align="center" spacing="md">
          <IconBook size={32} color={theme.colors.gray[5]} />
          <Text color="dimmed" size="sm" align="center">
            No courses generated yet. Start by creating your first course!
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack spacing={0}>
      {!collapsed && (
        <Box mb="md">
          <Group spacing="xs" mb="xs">
            <Badge color="blue" variant="dot">In Progress</Badge>
            <Text size="xs" color="dimmed">
              {courses.filter(c => c.state === 'in_progress').length} courses
            </Text>
          </Group>
          <Group spacing="xs" mb="xs">
            <Badge color="green" variant="dot">Completed</Badge>
            <Text size="xs" color="dimmed">
              {courses.filter(c => c.state === 'completed').length} courses
            </Text>
          </Group>
          <Group spacing="xs">
            <Badge color="gray" variant="dot">Not Started</Badge>
            <Text size="xs" color="dimmed">
              {courses.filter(c => c.state === 'not_started').length} courses
            </Text>
          </Group>
        </Box>
      )}

      {courses.sort((a, b) => {
        // Sort by state priority: in_progress -> not_started -> completed
        const stateOrder = { 'in_progress': 0, 'not_started': 1, 'completed': 2 };
        const stateCompare = stateOrder[a.state] - stateOrder[b.state];
        if (stateCompare !== 0) return stateCompare;
        
        // If same state, sort by creation date (newest first)
        return b.createdAt.getTime() - a.createdAt.getTime();
      }).map((course) => (
        <CourseHistoryItem
          key={course.id}
          course={course}
          collapsed={collapsed}
        />
      ))}
    </Stack>
  );
};

export default CourseHistory; 